import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { ProductDto, ProductsApiResponse, VariantDto, VariantsApiResponse } from '../../types';
import styles from './Inventaire.module.css';

export const Inventaire: React.FC = () => {
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [variants, setVariants] = useState<VariantDto[]>([]);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [variantCounts, setVariantCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Filtres
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [diffOnly, setDiffOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  const [showVariants, setShowVariants] = useState(false);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const [prodRes, varRes] = await Promise.all([
        api.get<ProductsApiResponse>('products/admin'),
        api.get<VariantsApiResponse>('variants'),
      ]);
      const data = prodRes.data?.data?.products ?? [];
      const varData = varRes.data?.data ?? [];

      setProducts(data);
      setVariants(varData);

      const initProd: Record<number, number> = {};
      data.forEach((p) => { initProd[p.id] = p.stock; });
      setCounts(initProd);

      const initVar: Record<number, number> = {};
      varData.forEach((v) => { initVar[v.id] = v.stock; });
      setVariantCounts(initVar);
    } catch (err: any) {
      setError('Erreur de chargement : ' + (err.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => { if (p.category) set.add(p.category); });
    return Array.from(set).sort();
  }, [products]);

  const filtered = useMemo(() => {
    let list = [...products];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.category || '').toLowerCase().includes(q) ||
          (p.subCategory || '').toLowerCase().includes(q)
      );
    }

    if (categoryFilter) list = list.filter((p) => p.category === categoryFilter);

    if (diffOnly) {
      list = list.filter((p) => {
        const prodDiff = (counts[p.id] ?? p.stock) !== p.stock;
        const variantDiff = showVariants && variants
          .filter((v) => v.productId === p.id)
          .some((v) => (variantCounts[v.id] ?? v.stock) !== v.stock);
        return prodDiff || variantDiff;
      });
    }

    switch (sortBy) {
      case 'name-asc':   list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'name-desc':  list.sort((a, b) => b.name.localeCompare(a.name)); break;
      case 'stock-asc':  list.sort((a, b) => a.stock - b.stock); break;
      case 'stock-desc': list.sort((a, b) => b.stock - a.stock); break;
      case 'diff-desc':  list.sort((a, b) => {
        const da = Math.abs((counts[a.id] ?? a.stock) - a.stock);
        const db = Math.abs((counts[b.id] ?? b.stock) - b.stock);
        return db - da;
      }); break;
    }

    return list;
  }, [products, variants, counts, variantCounts, search, categoryFilter, diffOnly, sortBy, showVariants]);

  const diffsCount = useMemo(() => {
    const prodDiffs = products.filter((p) => (counts[p.id] ?? p.stock) !== p.stock).length;
    const varDiffs = showVariants
      ? variants.filter((v) => (variantCounts[v.id] ?? v.stock) !== v.stock).length
      : 0;
    return prodDiffs + varDiffs;
  }, [products, variants, counts, variantCounts, showVariants]);

  const handleApply = async () => {
    const prodDiffs = products.filter((p) => (counts[p.id] ?? p.stock) !== p.stock);
    const varDiffs = showVariants
      ? variants.filter((v) => (variantCounts[v.id] ?? v.stock) !== v.stock)
      : [];

    if (prodDiffs.length === 0 && varDiffs.length === 0) {
      alert('Aucune différence à appliquer.');
      return;
    }

    const total = prodDiffs.length + varDiffs.length;
    if (!window.confirm(`Appliquer ${total} correction(s) de stock ?`)) return;

    setSaving(true);
    try {
      for (const p of prodDiffs) {
        await api.put(`products/${p.id}`, { ...p, stock: counts[p.id] });
      }
      for (const v of varDiffs) {
        await api.put(`variants/${v.id}`, { ...v, stock: variantCounts[v.id] });
      }
      alert(`${total} stock(s) corrigé(s).`);
      await loadProducts();
    } catch (err: any) {
      alert('Erreur : ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const resetP: Record<number, number> = {};
    products.forEach((p) => { resetP[p.id] = p.stock; });
    setCounts(resetP);

    const resetV: Record<number, number> = {};
    variants.forEach((v) => { resetV[v.id] = v.stock; });
    setVariantCounts(resetV);
  };

  return (
    <Layout>
      <div className={styles.container}>

        {/* EN-TÊTE */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Inventaire</h1>
            <span className={styles.badge}>
              {diffsCount > 0 ? `${diffsCount} différence${diffsCount > 1 ? 's' : ''}` : 'Aucune différence'}
            </span>
          </div>
          <div className={styles.headerActions}>
            <button onClick={handleReset} className={styles.btnReset} disabled={saving}>
              Réinitialiser
            </button>
            <button onClick={handleApply} className={styles.btnApply} disabled={saving}>
              {saving ? 'Enregistrement...' : `Appliquer${diffsCount > 0 ? ` (${diffsCount})` : ''}`}
            </button>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* BARRE DE FILTRES */}
        <div className={styles.filters}>
          <input
            type="text"
            placeholder="Rechercher par nom, catégorie..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.select}
          >
            <option value="">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.select}
          >
            <option value="name-asc">Nom A → Z</option>
            <option value="name-desc">Nom Z → A</option>
            <option value="stock-asc">Stock croissant</option>
            <option value="stock-desc">Stock décroissant</option>
            <option value="diff-desc">Écart le plus grand</option>
          </select>

          <label className={styles.checkLabel}>
            <input
              type="checkbox"
              checked={diffOnly}
              onChange={(e) => setDiffOnly(e.target.checked)}
            />
            Différences uniquement
          </label>

          <label className={`${styles.checkLabel} ${styles.checkLabelVariant}`}>
            <input
              type="checkbox"
              checked={showVariants}
              onChange={(e) => setShowVariants(e.target.checked)}
            />
            Afficher les variantes
          </label>

          <span className={styles.count}>
            {filtered.length} / {products.length} produits
          </span>
        </div>

        {/* GRILLE */}
        {loading ? (
          <div className={styles.loading}>Chargement de l'inventaire...</div>
        ) : (
          <div className={styles.grid}>
            {filtered.map((product) => {
              const counted = counts[product.id] ?? product.stock;
              const diff = counted - product.stock;
              const hasDiff = diff !== 0;
              const productVariants = variants.filter((v) => v.productId === product.id);
              const hasVariantDiff = showVariants && productVariants.some(
                (v) => (variantCounts[v.id] ?? v.stock) !== v.stock
              );

              return (
                <div
                  key={product.id}
                  className={`${styles.card} ${(hasDiff || hasVariantDiff) ? styles.cardDiff : ''}`}
                >
                  {/* MINIATURE */}
                  <div className={styles.imgWrap}>
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className={styles.img}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove(styles.hidden);
                        }}
                      />
                    ) : null}
                    <div className={`${styles.imgFallback} ${product.image ? styles.hidden : ''}`}>
                      📦
                    </div>
                  </div>

                  {/* INFOS */}
                  <div className={styles.info}>
                    <div className={styles.productName} title={product.name}>
                      {product.name}
                    </div>
                    {product.category && (
                      <div className={styles.category}>{product.category}</div>
                    )}
                  </div>

                  {/* STOCK PRODUIT */}
                  <div className={styles.stocks}>
                    <div className={styles.stockBlock}>
                      <span className={styles.stockLabel}>Stock actuel</span>
                      <span className={styles.stockValue}>{product.stock}</span>
                    </div>

                    <div className={styles.stockBlock}>
                      <span className={styles.stockLabel}>Stock compté</span>
                      <input
                        type="number"
                        value={counted}
                        min={0}
                        onChange={(e) =>
                          setCounts((prev) => ({
                            ...prev,
                            [product.id]: parseInt(e.target.value) || 0,
                          }))
                        }
                        className={`${styles.stockInput} ${hasDiff ? styles.stockInputDiff : ''}`}
                      />
                    </div>

                    <div className={styles.diffBlock}>
                      {hasDiff ? (
                        <span className={diff > 0 ? styles.diffPos : styles.diffNeg}>
                          {diff > 0 ? `+${diff}` : diff}
                        </span>
                      ) : (
                        <span className={styles.diffEq}>✓</span>
                      )}
                    </div>
                  </div>

                  {/* VARIANTES */}
                  {showVariants && productVariants.length > 0 && (
                    <div className={styles.variantsList}>
                      {productVariants.map((v) => {
                        const vCounted = variantCounts[v.id] ?? v.stock;
                        const vDiff = vCounted - v.stock;
                        const vHasDiff = vDiff !== 0;
                        return (
                          <div key={v.id} className={`${styles.variantRow} ${vHasDiff ? styles.variantRowDiff : ''}`}>
                            <span className={styles.variantName} title={v.name}>{v.name || '—'}</span>
                            <span className={styles.variantStock}>{v.stock}</span>
                            <input
                              type="number"
                              value={vCounted}
                              min={0}
                              onChange={(e) =>
                                setVariantCounts((prev) => ({
                                  ...prev,
                                  [v.id]: parseInt(e.target.value) || 0,
                                }))
                              }
                              className={`${styles.variantInput} ${vHasDiff ? styles.variantInputDiff : ''}`}
                            />
                            <span className={styles.variantDiff}>
                              {vHasDiff
                                ? <span className={vDiff > 0 ? styles.diffPos : styles.diffNeg}>{vDiff > 0 ? `+${vDiff}` : vDiff}</span>
                                : <span className={styles.diffEq}>✓</span>
                              }
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {showVariants && productVariants.length === 0 && (
                    <div className={styles.noVariants}>Aucune variante</div>
                  )}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className={styles.noResults}>Aucun produit trouvé.</div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};
